// ENG-10510 — give a quarantined channel a path back to active without a human
// click, and bound it so a dead channel cannot flap forever.
//
// ## Why this is time-based and not health-based
//
// ENG-5932's quarantine works by REMOVING the channel from the agent's
// effective set: `manager-worker.ts` drops quarantined keys from `.mcp.json`
// and from the launch flags. The channel's MCP process is therefore never
// spawned again, the reaper's liveness-reset block can never fire for that key
// (it is no longer in the declared set), and the heartbeat diagnostics only
// report the marker back up. **A quarantined channel has no health signal at
// all — its liveness is unobservable by construction.**
//
// The integration side (ENG-8036) can resume on health because a quarantined
// integration is still probeable out-of-band, through a reconstructed
// `inline_server_entry` (ENG-8055/8056). There is no channel equivalent and no
// scaffolding for one, so "resume when healthy" is not available here without
// building a channel prober first.
//
// So the only honest trigger is ELAPSED TIME plus a TRIAL RE-ADD: put the
// channel back, let it run, and let the reaper re-quarantine it if it is still
// dead. That is weaker than health-based resume and is named as such rather
// than dressed up.
//
// ## Why the floor is two hours, which is the load-bearing number here
//
// A failed trial is not free. Re-adding the channel flips `channelsChanged`,
// which costs ONE restart — the 0-restart exemption at `manager-worker.ts` is
// removal-only, an add is not exempt — plus up to
// `MAX_PRESENCE_RESTART_ATTEMPTS` (3) reaper restarts before it gives up again.
//
// WHICH TALLY THAT RESTART LANDS ON IS THE WHOLE ARGUMENT, and the first
// version of this comment got it wrong (Obi-Wan on #6313). The restart is
// scheduled with `breakerReason: 'channel-set-change'`, and that reason was in
// no class set in `restart-breaker.ts`, so `restartReasonClass` fell it through
// to the catch-all CRASH class: `DEFAULT_MAX` (2) per `DEFAULT_WINDOW_MS` (10
// minutes). This comment cited the provisioning budget — 5 per 30 minutes —
// which is 2.5x looser and 3x longer than the tally it actually landed on.
//
// The failure that made it matter: an agent with a second crash-class restart
// in the same ten minutes (the presence reaper's 'mcp-presence-reaper' once
// `activeKeys >= 2`) crosses 2 crash events during a trial and AUTO-PAUSES with
// no human in the loop. That is the ENG-8215 harm this floor exists to prevent,
// delivered by the feature meant to prevent it — and the ENG-9138 category
// error verbatim, which cost katie 8h16m.
//
// ENG-10510 fixed the CLASSIFICATION rather than shrinking the cadence to fit a
// misclassification: 'channel-set-change' is now in `PROVISIONING_RELOAD_REASONS`,
// where it always belonged as 'integration-change's sibling. So the budget
// genuinely is `RESTART_BREAKER_PROVISIONING_MAX` (5) per
// `RESTART_BREAKER_PROVISIONING_WINDOW_MS` (30 minutes), and a retry cadence
// anywhere near that window would still pause the agent. Two hours leaves it to
// drain completely between attempts, with headroom for an operator restart in
// the same period.
//
// ## WHAT THIS FLOOR DOES NOT FIX, SAID PLAINLY
//
// The paragraph above used to end by concluding the unattended auto-pause was
// prevented. It is not, and the enumeration at the top of this section is the
// reason: a failed trial costs ONE 'channel-set-change' restart PLUS up to
// MAX_PRESENCE_RESTART_ATTEMPTS reaper restarts, and only the first of those
// was reclassified. `reaperRestartBreakerReason` returns 'mcp-presence-reaper'
// whenever `activeKeys >= 2` (restart-breaker.ts), which is a CRASH reason on
// the tight tally: DEFAULT_MAX 2 per DEFAULT_WINDOW_MS 600_000.
//
// So on an agent that already carries one other declared-but-dead MCP, a single
// trial re-adds the dead channel, takes activeKeys to 2, and the reaper's first
// BACKOFF_AFTER_RESTARTS restarts have backoffMs 0 - three or four counted
// crash events inside one ten-minute window, which crosses the crash bar and
// AUTO-PAUSES the agent with no human in the loop. Verbatim the ENG-8215 harm
// this floor exists to prevent.
//
// NO RETRY CADENCE CAN HELP, because all of those restarts happen inside ONE
// trial. Spacing trials further apart does not reduce the count within a trial.
// That is why this is recorded here rather than absorbed by widening the floor:
// the fix is not a number in this file, it is either a guard on starting a
// trial that would take activeKeys to 2, or an attribution change in the reaper
// so its restarts during a trial are not crash-class - and the reaper's
// classification is a shared control, not this feature's to reclassify on its
// own. Filed as ENG-10861 with the measurement.
//
// Found by obi-wan on #6313 (`a8808b57dde4`) against the version of this
// comment that claimed the opposite. The claim was the defect: the derivation
// read as proven while the reachable half was unguarded, and a reader would
// have taken the floor as covering it.
//
// THIS DERIVATION IS PINNED, NOT JUST WRITTEN DOWN. `eng-10510-channel-auto-resume.test.ts`
// asserts `restartReasonClass('channel-set-change') === 'provisioning'` next to
// the floor assertions, so a future reclassification breaks the derivation
// loudly here instead of silently invalidating this paragraph — which is
// exactly how it was wrong the first time.
//
// ## Why it escalates and then stops
//
// ENG-9598 bounded the integration side's auto-resume because "an unbounded
// count would eventually quarantine a healthy integration forever". The same
// reasoning inverted: an unbounded retry would flap a dead channel forever, so
// each attempt doubles the wait and the fifth gives up and leaves it for a
// human. The manual Re-enable is untouched and remains the escape hatch.

import { readFileSync } from 'node:fs';
import { join } from 'node:path';
import { atomicWriteFileSync } from './atomic-write.js';
import { DEAD_CHANNEL_MS } from './mcp-presence-reaper.js';

/** First trial waits this long after the quarantine was set. */
export const AUTO_RESUME_BASE_MS = 2 * 60 * 60_000; // 2h

/** Ceiling on the doubling, so the wait stays a wait and not an abandonment. */
export const AUTO_RESUME_MAX_MS = 24 * 60 * 60_000; // 24h

/**
 * Trials before we stop and leave it quarantined for a person. Five attempts
 * at 2/4/8/16/24h spans just over two days — long enough for a transient host
 * or credential fault to clear, short enough that nobody is waiting a week.
 */
export const AUTO_RESUME_MAX_ATTEMPTS = 5;

/**
 * The dwell an optional channel must sit continuously missing before it is
 * quarantine-eligible. Mirrors `channelQuarantineDwellMs()`'s council-locked
 * default in manager-worker.ts; re-stated rather than imported because that
 * function reads an env override at call time and this derivation needs the
 * FLOOR, not whatever a host was configured with.
 */
const QUARANTINE_DWELL_FLOOR_MS = 180_000;

/**
 * How long after a trial we wait before calling it a SUCCESS.
 *
 * The strike count must be forgotten when a trial works, or the five-attempt
 * cap is a LIFETIME budget per (agent, channel) rather than a per-episode one
 * (Obi-Wan on #6313): `recordAttempt` only ever increments, `forget` had a
 * single caller on the operator re-enable path, so a channel that recovered
 * still carried its strikes into the next episode weeks later.
 *
 * THE SIGNAL CANNOT BE "no marker on the next poll". That races the trial
 * itself: the marker is gone the instant we clear it, the session has not
 * respawned yet, and the reaper has not had a chance to look. Every trial would
 * read as an immediate success and the cap would never apply to anything.
 *
 * AND THE FIRST DERIVATION OF THIS NUMBER WAS WRONG, IN THE DIRECTION THAT
 * BREAKS THE CAP (Obi-Wan on #6313, round 2). It was
 * `4 * MAX_PRESENCE_RESTART_ATTEMPTS * DEFAULT_COLD_START_GRACE_MS` - 18
 * minutes - on the premise that the reaper needs "3 attempts x 90s" to
 * re-quarantine a still-dead channel. Three things make the real floor far
 * larger:
 *
 *  1. Give-up is `state.attempts > MAX_PRESENCE_RESTART_ATTEMPTS` (strictly
 *     greater), so it takes FOUR counted generations, not three.
 *  2. The trial's own restart is a `channel-set-change`, which manager-worker
 *     lists as an EXTERNAL cause. `lastRestartWasExternal` is therefore true
 *     for the first post-trial generation, and `suppressAsChurn` skips the
 *     attempts increment AND re-bases `firstMissingAt` for it - so generation
 *     one contributes zero attempts and zero dwell.
 *  3. Quarantine additionally needs the dwell to elapse from a firstMissingAt
 *     that is no longer being re-based.
 *
 * So an 18-minute grace sat exactly ON the boundary it was meant to clear. A
 * failed trial would routinely be recorded as a SUCCESS, `forget()` would wipe
 * the strikes, and the next episode would start at attempt 1 - forever. A
 * permanently dead channel re-added every two hours, `attempts` never reaching
 * five, the give-up branch unreachable, and a log line asserting success each
 * time. That is precisely the unbounded flap this module's header says the cap
 * exists to prevent, reintroduced by the mechanism meant to bound it.
 *
 * DERIVED FROM THE REAL BOUND INSTEAD. `DEAD_CHANNEL_MS` is the one number the
 * churn suppression cannot outlast: once a key has been continuously missing
 * that long the reaper marks it genuinely dead and overrides storm-awareness
 * specifically so it CAN reach give-up and quarantine (ENG-8062). So
 * `DEAD_CHANNEL_MS + the dwell` is a true lower bound on re-quarantine, and the
 * grace is twice it.
 *
 * THE MARGIN IS DELIBERATELY GENEROUS BECAUSE THE ERRORS ARE NOT SYMMETRIC.
 * A late forget costs one longer wait on the next episode. An early forget
 * costs the bound itself - an unbounded flap that never gives up.
 *
 * AND THE PARAGRAPH THAT USED TO END HERE CONTRADICTED ITSELF (obi-wan
 * `5e4ac39e3e30`). It said a residual race remains on a busy agent whose reaper
 * restarts are held by defer-until-idle, and called that "survivable in the
 * late direction". It is not: a LATE re-quarantine is an EARLY forget, which
 * the two sentences above correctly identify as the direction that costs the
 * bound itself. I wrote the right rule and then applied it backwards to the one
 * case I had noticed.
 *
 * TWO STATES BREAK THE INFERENCE, and neither is a race - both are documented
 * behaviour:
 *
 *   1. ENG-10500's witness gate. The reaper deliberately WITHHOLDS quarantine
 *      while no declared stdio MCP server is live, because the restart budget
 *      was exhausted by a session that cannot spawn anything rather than by
 *      this channel dying. That is exactly the state where a trial proves
 *      nothing - and the marker never reappears, so every trial logs SUCCEEDED
 *      and the five-attempt cap can never bind.
 *   2. The reaper's own restarts for a dead channel are `channel-mcp-down`,
 *      window-exempt but still defer-until-idle, so a continuously busy agent
 *      can push the four generations needed for re-quarantine well past the
 *      grace however generous the multiplier is.
 *
 * NO MULTIPLIER FIXES EITHER ONE, which is why this is written down rather than
 * absorbed by doubling the grace again: case 1 has no re-quarantine to be late,
 * and case 2 is bounded by the agent's idleness rather than by any duration.
 * The real fix is to require POSITIVE evidence that the channel came back - the
 * key present and live in the running set - instead of inferring recovery from
 * the absence of a marker. That needs liveness the decision site is not
 * currently handed, so it is ENG-10870 rather than a change smuggled into this
 * comment.
 *
 * The multiplier stays at 2 because it is still right for the ordinary case it
 * was derived for.
 *
 * It must stay well under `AUTO_RESUME_BASE_MS`, or a success could not be
 * declared before the next trial was already due. Both bounds are asserted in
 * the tests rather than left to arithmetic in a comment - and the test now
 * checks the ABSOLUTE value too, because the previous one compared the constant
 * against the same understated product that produced it and could not have
 * caught this.
 */
export const AUTO_RESUME_SUCCESS_GRACE_MS = 2 * (DEAD_CHANNEL_MS + QUARANTINE_DWELL_FLOOR_MS); // 36 minutes

/**
 * ...AND THE ABOVE IS THE DEFAULT, NOT THE RULE (obi-wan `7414dbf3d758`).
 *
 * The constant is derived from the dwell FLOOR, which is right for a host that
 * has not overridden the dwell and wrong for one that has raised it. Callers
 * pass the configured dwell to {@link shouldForgetResumeHistory} and the grace
 * is recomputed by {@link autoResumeSuccessGraceMs}; this constant remains the
 * pinned default the tests assert against.
 */

/** What an auto-resume attempt records, per agent + channel key. */
export interface ResumeHistoryEntry {
  /** How many auto-resume trials have been made for this key. */
  attempts: number;
  /** Epoch ms of the most recent trial. */
  lastAttemptAt: number;
  /**
   * Have we already told an operator this key was abandoned?
   *
   * A once-per-episode latch, the same shape the reaper's `gaveUpLogged` /
   * `quarantineLogged` use. It lives on the history entry so it resets exactly
   * when the strikes do - an operator Re-enable, or a successful trial - which
   * is the correct lifetime: a NEW episode that later exhausts its budget is a
   * new thing worth saying, and the same episode is not.
   *
   * Optional so an older sidecar written before this field still validates.
   */
  gaveUpLogged?: boolean;
}

type ResumeFile = Record<string, Record<string, ResumeHistoryEntry>>;

/**
 * A SEPARATE FILE, deliberately, and this is a safety decision rather than a
 * tidiness one.
 *
 * The obvious alternative is to version `channel-quarantine.json` and carry
 * resume history inside it. That is a fleet-wide hazard on a rolling deploy:
 * `isQuarantineFile` validates EVERY value and rejects the whole file if any
 * entry lacks `quarantinedAt`/`reason`, and `load()` falls back to `{}` on a
 * rejected file. So an older manager reading a new-shaped file would treat
 * every channel as un-quarantined and re-arm the ENG-5932 flap on every agent
 * it manages — the exact outage quarantine exists to prevent, caused by the
 * feature meant to end it.
 *
 * A sidecar file cannot do that. An old manager never opens it; a new manager
 * missing it fails open to "no history", which costs at most one extra trial.
 */
export function defaultResumeHistoryPath(configDir: string): string {
  return join(configDir, 'channel-quarantine-resumes.json');
}

function isResumeFile(value: unknown): value is ResumeFile {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  for (const forAgent of Object.values(value as Record<string, unknown>)) {
    if (!forAgent || typeof forAgent !== 'object' || Array.isArray(forAgent)) return false;
    for (const entry of Object.values(forAgent as Record<string, unknown>)) {
      if (!entry || typeof entry !== 'object') return false;
      const e = entry as Record<string, unknown>;
      // NON-NEGATIVE SAFE INTEGERS, not merely numbers (CodeRabbit on #6313).
      //
      // A bare `typeof === 'number'` admits `-100`, `1.5` and `NaN`. The
      // negative case is the dangerous one and it defeats the entire point of
      // this file: `backoffForAttempt` clamps a negative count to the 2h floor
      // via `Math.max(0, ...)`, and the give-up check is `attempts >= MAX`, so
      // a persisted `attempts: -100` buys a hundred extra trials at the
      // shortest wait — an unbounded flap granted by the very record that
      // exists to bound it. A corrupt write or a hand-edit is enough.
      //
      // Rejecting the whole file rather than the entry, consistent with the
      // fail-open contract above: no history costs at most one extra trial and
      // the cap re-applies from the next write, whereas trusting a malformed
      // count costs the bound itself.
      // `gaveUpLogged` is optional and must be a boolean when present. A
      // non-boolean is a corrupt write like any other, and the file-level
      // rejection below is the same fail-open contract.
      if (e.gaveUpLogged !== undefined && typeof e.gaveUpLogged !== 'boolean') return false;
      const attempts = e.attempts;
      const lastAttemptAt = e.lastAttemptAt;
      if (
        typeof attempts !== 'number' ||
        !Number.isSafeInteger(attempts) ||
        attempts < 0 ||
        typeof lastAttemptAt !== 'number' ||
        !Number.isSafeInteger(lastAttemptAt) ||
        lastAttemptAt < 0
      ) {
        return false;
      }
    }
  }
  return true;
}

/**
 * Resume history, persisted beside the quarantine markers and OUTLIVING them.
 *
 * It has to outlive them: `clearQuarantine` deletes the marker, so a strike
 * counter kept inside `QuarantineEntry` would vanish on the very event it is
 * counting. Every re-quarantine would then look like the first, the backoff
 * would never escalate, and a permanently dead channel would be re-added every
 * two hours forever — this ticket's bug with extra steps.
 */
export class ChannelResumeHistoryStore {
  private readonly path: string;
  private cache: ResumeFile | null = null;

  constructor(path: string) {
    this.path = path;
  }

  private load(): ResumeFile {
    if (this.cache) return this.cache;
    try {
      const parsed = JSON.parse(readFileSync(this.path, 'utf-8')) as unknown;
      this.cache = isResumeFile(parsed) ? parsed : {};
    } catch {
      // Fail open to "no history". Worst case one extra trial, bounded by the
      // attempt cap the moment the file is writable again.
      this.cache = {};
    }
    return this.cache;
  }

  private persist(): void {
    atomicWriteFileSync(this.path, JSON.stringify(this.cache ?? {}, null, 2));
  }

  get(codeName: string, serverKey: string): ResumeHistoryEntry | undefined {
    return this.load()[codeName]?.[serverKey];
  }

  /**
   * Every recorded key for one agent, as [storedKey, entry] pairs.
   *
   * Needed because the success sweep asks about channels that are NOT in the
   * quarantine store — a trial that worked leaves history behind and no marker,
   * so a loop over quarantine entries can never reach it. Returns the STORED
   * key (see `resumeHistoryKey`), not the channel key, because the caller is
   * the only thing that knows which namespace it is working in.
   */
  entriesFor(codeName: string): Array<[string, ResumeHistoryEntry]> {
    return Object.entries(this.load()[codeName] ?? {});
  }

  /** Record a trial. Returns the entry as written. */
  recordAttempt(codeName: string, serverKey: string, now: number): ResumeHistoryEntry {
    const file = this.load();
    const forAgent = (file[codeName] ??= {});
    const prior = forAgent[serverKey];
    const next: ResumeHistoryEntry = {
      attempts: (prior?.attempts ?? 0) + 1,
      lastAttemptAt: now,
      // Carried, not reset: a further trial inside the same episode must not
      // re-arm the give-up line. It clears with the entry itself on forget().
      ...(prior?.gaveUpLogged ? { gaveUpLogged: true } : {}),
    };
    forAgent[serverKey] = next;
    this.persist();
    return next;
  }

  /**
   * Forget a key's history — called when an OPERATOR clears the quarantine.
   *
   * A human pressing Re-enable is a statement that the underlying fault was
   * addressed, so the next automatic trial should start from a clean two hours
   * rather than inheriting strikes from before the fix. Without this an
   * operator could fix a channel, watch it break again for an unrelated reason
   * weeks later, and find auto-resume already exhausted.
   */
  forget(codeName: string, serverKey: string): boolean {
    const file = this.load();
    const forAgent = file[codeName];
    if (!forAgent || !forAgent[serverKey]) return false;
    delete forAgent[serverKey];
    if (Object.keys(forAgent).length === 0) delete file[codeName];
    this.persist();
    return true;
  }

  /**
   * Latch the give-up line for this key. Returns whether it had ALREADY been
   * logged, so the caller logs only on the first call of an episode.
   *
   * Returns true (already logged) when there is no entry at all: with no
   * history there are no strikes, so there is nothing to have given up ON, and
   * announcing an abandonment would be false.
   */
  noteGaveUpLogged(codeName: string, serverKey: string): boolean {
    const file = this.load();
    const entry = file[codeName]?.[serverKey];
    if (!entry) return true;
    if (entry.gaveUpLogged) return true;
    entry.gaveUpLogged = true;
    this.persist();
    return false;
  }

  __invalidateCacheForTests(): void {
    this.cache = null;
  }
}

/**
 * The key a mode's strikes are stored under.
 *
 * SHADOW STRIKES ARE NAMESPACED SEPARATELY, and this is the flip that would
 * otherwise be wrong (Obi-Wan on #6313). Shadow has to call `recordAttempt` —
 * without it `history` stays undefined forever, the decision is pinned at
 * `{action:'resume', attempt:1}`, and the branch logs the same "WOULD resume"
 * line every poll: ~8,600 lines a day per channel at the 10s default interval,
 * while never once demonstrating the escalation or the cap it exists to
 * preview. A shadow that cannot escalate or give up does not model the enforce
 * path at all, which is the only reason shadow exists.
 *
 * But recording into the SAME key would then poison the flip it is meant to
 * de-risk: an operator who ran shadow for a day and flipped to enforce would
 * start at attempt 4 with a 16h wait, having performed zero real trials. The
 * ladder would be spent on a simulation.
 *
 * So shadow keeps its own namespace. It models the full ladder, it stops at the
 * cap, and enforce starts clean — because a simulated trial is not evidence
 * about the channel, only about the schedule.
 */
export function resumeHistoryKey(mode: 'shadow' | 'enforce', serverKey: string): string {
  return mode === 'shadow' ? `shadow:${serverKey}` : serverKey;
}

/** How long to wait before the Nth trial (N = attempts already made). */
export function backoffForAttempt(attemptsAlreadyMade: number): number {
  const doubled = AUTO_RESUME_BASE_MS * 2 ** Math.max(0, attemptsAlreadyMade);
  return Math.min(doubled, AUTO_RESUME_MAX_MS);
}

export type AutoResumeDecision =
  | { action: 'resume'; waitedMs: number; attempt: number }
  | { action: 'wait'; dueInMs: number; attempt: number }
  | { action: 'give-up'; attempts: number };

/**
 * THE DECISION, kept pure so it can be proven without a manager, a clock or a
 * filesystem.
 *
 * `since` is the later of the quarantine time and the last trial: a trial that
 * failed restarts the clock, so a channel re-quarantined moments after a trial
 * does not immediately become due again on its original timestamp.
 */
/**
 * Did a trial SUCCEED, so its strikes should be forgotten?
 *
 * Pure, and separate from `decideChannelAutoResume`, because it answers a
 * question about a key that has NO quarantine entry — so it cannot be reached
 * from a loop that iterates quarantine entries. Kept here so the two halves of
 * the strike lifecycle sit together and can be proven without a manager.
 *
 * True only when all three hold:
 *   - there is history to forget (otherwise this is a no-op write every poll),
 *   - the key is not currently quarantined (a re-quarantine means the trial
 *     FAILED, and those strikes must stand), and
 *   - enough time has passed since the trial for the reaper to have
 *     re-quarantined it if it were still dead.
 */
/**
 * ENG-10510 (obi-wan `7b85a224e858`): the effective auto-resume mode, given
 * BOTH flags.
 *
 * `channel-quarantine-auto-resume` and `channel-quarantine-mode` are two
 * independently flippable sensitive enums, and this feature read only its own.
 * Auto-resume's entire safety argument is "put the channel back and let the
 * reaper re-quarantine it if it is still dead" - and the reaper writes a marker
 * ONLY at `enforce`; at `shadow` it logs `SHADOW would-quarantine` and writes
 * nothing.
 *
 * So enforce-mode auto-resume against a non-enforce quarantine-mode clears
 * every marker permanently, `shouldForgetResumeHistory` sees no marker, and
 * after the grace each trial is scored a SUCCESS and its strikes wiped -
 * un-quarantining every dead channel, re-arming the ENG-5932 session flap, with
 * no error and a log line asserting the opposite. That combination is reachable
 * by de-escalating quarantine during an incident, or by flipping the two in the
 * wrong order during rollout.
 *
 * DOWNGRADED, NOT REFUSED. A trial whose failure cannot be observed is not a
 * trial, so this drops to `shadow`: the bookkeeping still runs and still
 * previews the escalation, and nothing is cleared. Refusing outright would
 * trade a dangerous no-op for an invisible one.
 *
 * Pure so it can be tested without a manager poll - the poll reads the two
 * flags and asks this.
 */
export function resolveAutoResumeEffectiveMode(
  autoResume: 'off' | 'shadow' | 'enforce',
  quarantine: 'off' | 'shadow' | 'enforce',
): 'off' | 'shadow' | 'enforce' {
  if (autoResume === 'enforce' && quarantine !== 'enforce') return 'shadow';
  return autoResume;
}

export function autoResumeSuccessGraceMs(configuredDwellMs?: number): number {
  const dwell =
    typeof configuredDwellMs === 'number' &&
    Number.isFinite(configuredDwellMs) &&
    configuredDwellMs > QUARANTINE_DWELL_FLOOR_MS
      ? configuredDwellMs
      : QUARANTINE_DWELL_FLOOR_MS;
  return 2 * (DEAD_CHANNEL_MS + dwell);
}

export function shouldForgetResumeHistory(input: {
  isQuarantined: boolean;
  history: ResumeHistoryEntry | undefined;
  now: number;
  /**
   * THE DWELL THIS HOST IS ACTUALLY CONFIGURED WITH (obi-wan `7414dbf3d758`).
   *
   * The grace was derived once from the hard-coded FLOOR, and the comment on
   * that constant argued the floor is the right input. For the SUCCESS
   * derivation it is not, and the two directions are not symmetric:
   * `channelQuarantineDwellMs()` accepts any non-negative
   * `AGT_CHANNEL_QUARANTINE_DWELL_MS`, and a LARGER configured dwell pushes
   * re-quarantine LATER, not earlier.
   *
   * With the dwell above ~21 minutes (DEAD_CHANNEL_MS 15m + dwell > the 36m
   * grace) a FAILED trial is still un-quarantined when this runs, so it reads
   * as a success, `forget()` wipes the strikes, and the next episode restarts
   * at attempt 1 with a 2h wait. Forever - which is precisely the unbounded
   * flap the five-attempt cap exists to prevent, delivered by the bookkeeping
   * meant to enforce it, while the log line says "trial SUCCEEDED".
   *
   * Omitted means "use the floor", which is what every caller that has not
   * configured an override effectively gets. The clamp is one-sided on purpose:
   * a dwell SHORTER than the floor cannot make the grace too short, so only a
   * longer one moves it.
   */
  configuredDwellMs?: number;
}): boolean {
  if (input.isQuarantined) return false;
  const history = input.history;
  if (!history || history.attempts === 0) return false;
  return input.now - history.lastAttemptAt >= autoResumeSuccessGraceMs(input.configuredDwellMs);
}

export function decideChannelAutoResume(input: {
  quarantinedAt: number;
  history: ResumeHistoryEntry | undefined;
  now: number;
}): AutoResumeDecision {
  const attempts = input.history?.attempts ?? 0;
  if (attempts >= AUTO_RESUME_MAX_ATTEMPTS) return { action: 'give-up', attempts };

  const since = Math.max(input.quarantinedAt, input.history?.lastAttemptAt ?? 0);
  const waited = input.now - since;
  const need = backoffForAttempt(attempts);
  if (waited >= need) return { action: 'resume', waitedMs: waited, attempt: attempts + 1 };
  return { action: 'wait', dueInMs: need - waited, attempt: attempts + 1 };
}
